import React, { useMemo, useState } from 'react';
import {
  useScenarioOrTotalProgramExportable,
  useWorksheet,
} from 'spw-programming-projects/downloads/exportables/hooks';
import { isMacro } from 'spw-components/worksheet/utils';
import MacroSinglePageSummary, {
  MacroCategorySummaryRow,
} from 'spw-programming-projects/program-summary/macro-single-page-summary';
import SinglePageSummary from 'spw-programming-projects/program-summary/single-page-summary';
import { useSelector } from 'react-redux';
import { IAppState } from 'spw-redux/reducer';
import { selectProgramSummaryCalculator } from 'spw-redux/modules/calculators/selectors';
import { isNil } from 'ramda';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';
import { useLocation } from 'react-router-dom-v5';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { CUSTOM_CATEGORY, INDIVIDUAL_SPACES_CATEGORY } from 'spw-constants';
import { colors } from 'spw-theme';
import { Header } from 'spw-programming-projects/reports/exportables/header';
import { useVisibleCards } from 'spw-programming-projects/reports/exportables/hooks';
import { ProgramSummaryContext } from 'spw-programming-projects/program-summary/program-summary-context';
import { CategorySummaryRow } from 'spw-programming-projects/program-summary/single-page-summary/CategorySummaryRow';
import { ProgrammingSummaryCardsContext } from 'spw-programming-projects/program-summary/program-summary-tabs/summary-card-context';

const ProgramSummaryExportable: React.FunctionComponent = () => {
  const location = useLocation();
  const {
    isTotalProgram,
    currentScenario,
    currentProject,
    projectSpaceTypes,
  } = useScenarioOrTotalProgramExportable();

  const currentProjectColors = useSelector(selectProjectColors);
  const programSummaryCalculator = useSelector((state: IAppState) =>
    selectProgramSummaryCalculator(state)
  );

  const urlParams = new URLSearchParams(location?.search);
  const category = urlParams.get('category');
  const showRsfParam = urlParams.get('show_rsf') === 'true';
  const [showRsf, setShowRsf] = useState(showRsfParam);

  const shouldShowClientFacingNames =
    urlParams.get('show_client_facing_names') === 'true';

  const { visibleCards, setVisibleCards } = useVisibleCards();

  const isOpen = urlParams.get('is_open') === 'true';
  const { worksheet } = useWorksheet(isTotalProgram);

  const shouldRender = useMemo(() => {
    return Boolean(
      currentScenario?.id &&
        currentProject?.id &&
        currentProject?.spaceTypeCategories?.length > 0 &&
        worksheet &&
        !isNil(programSummaryCalculator)
    );
  }, [currentScenario, currentProject, worksheet, programSummaryCalculator]);

  if (category && category !== INDIVIDUAL_SPACES_CATEGORY) {
    const projectSpaceTypeCategory = currentProject?.spaceTypeCategories?.find(
      (currentProjectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
        currentProjectSpaceTypeCategory.defaultSpaceTypeCategory.category ===
        category
    );

    const projectSpaceTypesAsObject = projectSpaceTypes.reduce(
      (acc, spaceType) => ({ ...acc, [spaceType.id]: spaceType }),
      {}
    );

    return shouldRender &&
      projectSpaceTypeCategory &&
      worksheet?.rowsByCategory[category]?.length > 0 ? (
      <div
        className="content"
        style={{ background: colors.lightGreyBackground, overflow: 'hidden' }}
      >
        <ProgramSummaryContext.Provider
          value={{
            visibleCards,
            setVisibleCards,
          }}
        >
          <Header />
          {isMacro(currentScenario) ? (
            <MacroCategorySummaryRow
              viewOnly
              projectSpaceTypeCategory={projectSpaceTypeCategory}
              projectColors={currentProjectColors}
              worksheet={worksheet}
              shouldShowChildrenRowsForDownload={isOpen}
              projectSpaceTypes={projectSpaceTypesAsObject}
              isRenderingForExport
              clientFacingNamesAreVisible={shouldShowClientFacingNames}
            />
          ) : (
            <CategorySummaryRow
              viewOnly
              projectSpaceTypeCategory={projectSpaceTypeCategory}
              projectColors={currentProjectColors}
              worksheet={worksheet}
              shouldShowChildrenRowsForDownload={isOpen}
              projectSpaceTypes={projectSpaceTypesAsObject}
              clientFacingNamesAreVisible={shouldShowClientFacingNames}
              isRenderingForExport
            />
          )}
        </ProgramSummaryContext.Provider>
      </div>
    ) : null;
  }

  if (category === INDIVIDUAL_SPACES_CATEGORY) {
    const spaceTypeCategories = currentProject?.spaceTypeCategories;

    return shouldRender && worksheet?.rowsByCategory[category]?.length > 0 ? (
      <div
        className="content"
        style={{ background: colors.lightGreyBackground, overflow: 'hidden' }}
      >
        <ProgramSummaryContext.Provider
          value={{
            visibleCards: {
              ...visibleCards,
              categoryUsfAndSeatMix: spaceTypeCategories
                .filter((spaceTypeCategory) => {
                  return ![
                    INDIVIDUAL_SPACES_CATEGORY,
                    CUSTOM_CATEGORY,
                  ].includes(
                    spaceTypeCategory.defaultSpaceTypeCategory.category
                  );
                })
                .reduce((usfAndSeatMix, spaceTypeCategory) => {
                  return {
                    ...usfAndSeatMix,
                    [spaceTypeCategory.defaultSpaceTypeCategory.category]: {
                      usfAndSeatMix: false,
                      usfMixSeatMixAndSubtotals: false,
                      usfMix: false,
                      seatMix: false,
                      seatMixSubtotals: false,
                      usfMixAndRatios: false,
                      ratios: false,
                    },
                  };
                }, visibleCards.categoryUsfAndSeatMix),
            },
            setVisibleCards,
          }}
        >
          <ProgrammingSummaryCardsContext.Provider
            value={{ setShowRsf, showRsf }}
          >
            <Header />
            {isMacro(currentScenario) ? (
              <MacroSinglePageSummary
                shouldShowChildrenRowsForDownload={isOpen}
                shouldShowRsfForDownload={showRsfParam}
                clientFacingNamesAreVisible={shouldShowClientFacingNames}
                isRenderingForExport
              />
            ) : (
              <SinglePageSummary
                shouldShowChildrenRowsForDownload={isOpen}
                clientFacingNamesAreVisible={shouldShowClientFacingNames}
                shouldShowRsfForDownload={showRsfParam}
                isRenderingForExport
              />
            )}
          </ProgrammingSummaryCardsContext.Provider>
        </ProgramSummaryContext.Provider>
      </div>
    ) : null;
  }

  return shouldRender ? (
    <div
      className="content"
      style={{ background: colors.lightGreyBackground, overflow: 'hidden' }}
    >
      <ProgramSummaryContext.Provider value={{ visibleCards, setVisibleCards }}>
        <ProgrammingSummaryCardsContext.Provider
          value={{ setShowRsf, showRsf }}
        >
          <Header showProjectName />
          {isMacro(currentScenario) ? (
            <MacroSinglePageSummary
              shouldShowChildrenRowsForDownload={isOpen}
              shouldShowRsfForDownload={showRsf}
              clientFacingNamesAreVisible={shouldShowClientFacingNames}
              isRenderingForExport
            />
          ) : (
            <SinglePageSummary
              shouldShowChildrenRowsForDownload={isOpen}
              clientFacingNamesAreVisible={shouldShowClientFacingNames}
              shouldShowRsfForDownload={showRsf}
              isRenderingForExport
            />
          )}
        </ProgrammingSummaryCardsContext.Provider>
      </ProgramSummaryContext.Provider>
    </div>
  ) : null;
};

export default ProgramSummaryExportable;
